import { isNotNullOrUndefined } from "@/app/apiutils";
import type { ModelObject } from "@/app/services/modelObject";
import type { ReadonlyEntityArray } from "@/components/composables/reactivityTypes";
import type { GridDragDropEvent } from "@/components/controls/grid/BmGrid.vue";
import { mergeConfiguration } from "@/components/platform/common/configurationUtils";
import type { ComputedRef, Ref } from "vue";

export interface DragDropOptions {
  allowMultipleSelection?: boolean;
}

export interface DragDropKeysOptions {
  allowMultipleSelection?: boolean;
  removeInvalidRows?: boolean;
}

export interface DragDropItemsOptions<T> {
  allowMultipleSelection?: boolean;
  sequenceUpdater?: (item: T, index: number) => void;
}

export const dragDropDefaults: DragDropOptions = {
  allowMultipleSelection: true,
};

/**
 * Provides drag and drop functionality and sorts keys in the order they are dropped.
 * Supports inter grid drag and drop.
 */
export const useGridKeysDragDrop = (
  keys: ComputedRef<string[]>,
  updateKeysCallback: (updatedKeyOrder: string[]) => Promise<void> | void,
  options: DragDropKeysOptions = {}
) => {
  const dragDropOptions = mergeConfiguration(dragDropDefaults, options);
  const onDrop = (event: GridDragDropEvent) => {
    const targetKey = event.droppedOnRowKey;
    let movedKeys = event.draggedRowKeys;
    if (!validateDragDropEvent(event, dragDropOptions)) {
      return;
    }

    if (options.removeInvalidRows) {
      const validKeys = movedKeys.filter((key) => keys.value.includes(key));
      if (validKeys.length !== movedKeys.length) {
        movedKeys = validKeys;
      }
    }

    const orderedKeys = addRowsAtItemPosition(
      keys.value,
      movedKeys,
      targetKey,
      (key) => key
    );

    updateKeysCallback(orderedKeys);
  };

  return { onDrop };
};

/**
 * Provides a drag and drop handler that returns the new order of the items post drag and drop.
 * Does not support inter grid drag and drop.
 */
export const useGridItemsDragDrop = <T>(
  items: Ref<ReadonlyEntityArray<T> | T[]>,
  rowKeyExtractor: (item: T) => string,
  updateItemsCallback: (updatedItemsOrder: T[]) => Promise<void> | void,
  options: DragDropItemsOptions<T> = {}
) => {
  const dragDropOptions: DragDropItemsOptions<T> = mergeConfiguration(
    options,
    dragDropDefaults
  );
  const onDrop = async (event: GridDragDropEvent) => {
    const orderedItems = evaluateDragDropResequence(
      items.value,
      rowKeyExtractor,
      event,
      dragDropOptions
    );

    if (!orderedItems) {
      // Invalid Drag-Drop
      return;
    }

    if (dragDropOptions.sequenceUpdater) {
      updateSequence(orderedItems);
    }

    await updateItemsCallback(orderedItems);
  };

  const updateSequence = (itemsToSequence: T[]) => {
    if (!dragDropOptions.sequenceUpdater) {
      return;
    }
    itemsToSequence.forEach((item, index) =>
      dragDropOptions.sequenceUpdater!(item, index)
    );
  };

  return {
    onDrop,
  };
};

const addRowsAtItemPosition = <T>(
  existingItems: ReadonlyEntityArray<T> | T[],
  rowsToAdd: T[],
  targetKey: string | undefined,
  rowKeyExtractor: (item: T) => string
) => {
  const rowsToAddKeys = rowsToAdd.map(rowKeyExtractor);
  const newItemsOrdered: T[] = [];

  if (targetKey === undefined || existingItems.length === 0) {
    // add the moved keys before the first row
    newItemsOrdered.push(...rowsToAdd);
  }
  // Reorder the items by moving all of the moved keys to below the target key.
  existingItems.forEach((row) => {
    const currentKey = rowKeyExtractor(row);

    if (!rowsToAddKeys.includes(currentKey)) {
      // If unchanged, add the row
      newItemsOrdered.push(row);
    }

    if (currentKey === targetKey) {
      // add the moved keys below the target key. Will include target if it was moved.
      newItemsOrdered.push(...rowsToAdd);
    }
  });
  return newItemsOrdered;
};

const validateDragDropEvent = (
  event: GridDragDropEvent,
  options: DragDropOptions
) => {
  if (
    // No items to move.
    !event.draggedRowKeys.length ||
    // Dropped on the same row.
    (event.draggedRowKeys.length === 1 &&
      event.draggedRowKeys[0] === event.droppedOnRowKey) ||
    // Dragged multiple items when not allowed.
    (!options.allowMultipleSelection && event.draggedRowKeys.length !== 1)
  ) {
    return false;
  }
  return true;
};

export const evaluateDragDropResequence = <T>(
  itemsInPreviousSequence: readonly T[],
  rowKeyExtractor: (item: T) => string,
  event: GridDragDropEvent,
  options: DragDropOptions = {}
): T[] | null => {
  if (!validateDragDropEvent(event, options)) {
    return null;
  }

  const movedKeys = event.draggedRowKeys;
  const targetKey = event.droppedOnRowKey;

  const movedRows = movedKeys
    .map((key) => {
      return itemsInPreviousSequence.find((i) => rowKeyExtractor(i) === key);
    })
    .filter(isNotNullOrUndefined);

  return addRowsAtItemPosition(
    itemsInPreviousSequence,
    movedRows,
    targetKey,
    rowKeyExtractor
  );
};

export const cloneItemsForNewSequence = <T extends ModelObject<T>>(
  itemsInPreviousSequence: T[],
  extractSequence: (item: T) => number,
  setSequence: (item: T, sequence: number) => void
) => {
  return itemsInPreviousSequence
    .map((item, idx) => ({
      item: item,
      idx: idx,
    }))
    .filter((x) => extractSequence(x.item) != x.idx)
    .map((x) => {
      const clone = x.item.clone();
      setSequence(clone, x.idx);
      return clone;
    });
};
